import { Fragment, useEffect, useState } from "react";
import { useReadContract, useWatchBlockNumber } from "wagmi";
import { formatEther, type Hex } from "viem";
import { COLOR_WHEEL_ADDRESS, contractsConfigured } from "../lib/chain";
import { COLORS, colorWheelAbi, resultFromEntropy } from "../lib/contracts";

export type RecentRound = {
  roundId: bigint;
  totalStaked: bigint;
  winPool: bigint;
  settleBlock: bigint;
  players: number;
  result: number;
  won: boolean;
  entropy: Hex;
};

type Props = {
  refreshKey?: number;
  /** When set (demo mode), show this local history instead of chain data. */
  override?: RecentRound[];
};

export function BetLog({ refreshKey = 0, override }: Props) {
  const [expanded, setExpanded] = useState<string | null>(null);

  const { data, refetch, isLoading, isError } = useReadContract({
    address: COLOR_WHEEL_ADDRESS,
    abi: colorWheelAbi,
    functionName: "getRecentRounds",
    query: { enabled: contractsConfigured && !override, refetchInterval: 15_000 },
  });

  useWatchBlockNumber({
    enabled: contractsConfigured && !override,
    onBlockNumber() {
      void refetch();
    },
  });

  useEffect(() => {
    if (refreshKey > 0 && !override) void refetch();
  }, [refreshKey, refetch, override]);

  const rounds = (override ?? (data as RecentRound[] | undefined) ?? []).slice(0, 10);

  return (
    <section className="feed" id="recent">
      <h2 className="section-title">RECENT SPINS</h2>

      {!contractsConfigured && !override ? (
        <p className="feed-empty">deploy contracts to see live spins</p>
      ) : !override && isLoading ? (
        <p className="feed-empty">loading…</p>
      ) : !override && isError ? (
        <p className="feed-empty">could not load spins</p>
      ) : rounds.length === 0 ? (
        <p className="feed-empty">no spins yet — be the first</p>
      ) : (
        <div className="feed-list">
          {rounds.map((round) => {
            const id = round.roundId.toString();
            const idle = round.entropy.length <= 2; // no bets — decorative spin
            const ok = idle || resultFromEntropy(round.entropy) === round.result;
            const open = expanded === id;
            return (
              <Fragment key={id}>
                <button
                  type="button"
                  className="feed-row"
                  onClick={() => setExpanded(open ? null : id)}
                >
                  <span className={`feed-coin ${round.won ? "" : "cold"}`} style={{ background: COLORS[0].hex }}>
                    <span className="feed-coin-num">{round.result + 1}</span>
                  </span>
                  <span className="feed-text">
                    round&nbsp;#{id.slice(-5)} ·{" "}
                    <strong style={{ color: COLORS[0].hex }}>
                      GREEN on P{round.result + 1}
                    </strong>
                    {" · "}
                    {idle
                      ? "no bets"
                      : round.won
                        ? `${round.players} player${round.players === 1 ? "" : "s"} · ${Number(
                            formatEther(round.totalStaked)
                          ).toFixed(4)} ETH staked`
                        : `nobody on it · ${round.players} player${
                            round.players === 1 ? "" : "s"
                          }`}
                  </span>
                  <span className={`feed-outcome ${round.won ? "good" : "bad"}`}>
                    {round.won
                      ? `${Number(formatEther(round.winPool * 10n)).toFixed(4)} ETH paid`
                      : idle
                        ? "idle"
                        : "treasury +"}
                  </span>
                </button>
                {open && idle && (
                  <div className="feed-proof">
                    <p>
                      no chips were placed this round — the spin was decorative, derived from
                      the round id. nothing settled on-chain.
                    </p>
                  </div>
                )}
                {open && !idle && (
                  <div className="feed-proof">
                    <p>
                      <strong>formula</strong>{" "}
                      <code>keccak256(blockhash(closeBlock + 1), roundId) % 10</code>
                    </p>
                    <p>
                      <strong>round id</strong> <code>{id}</code>
                    </p>
                    <p>
                      <strong>entropy</strong> <code>{round.entropy}</code>
                    </p>
                    <p>
                      <strong>settle block</strong> {round.settleBlock.toString()}
                    </p>
                    <p className={ok ? "proof-ok" : "proof-bad"}>
                      {ok
                        ? `verified: entropy % 10 = ${round.result} → green on position ${
                            round.result + 1
                          }`
                        : "MISMATCH — report this"}
                    </p>
                  </div>
                )}
              </Fragment>
            );
          })}
        </div>
      )}
    </section>
  );
}
